import { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { pwj30bApi, LabelRequest, PrintResult } from '../../../services/pwj30bApi';

export interface LabelFormValues {
  invoice_no: string;
  order_no: string;
  article: string;
  color: string;
  finish: string;
  piece_no: string;
  qty_m: string;
  grade: string;
  label_date: dayjs.Dayjs | null;
  inspector: string;
  qty_yd: string;
  print_copies: number;
}

const defaultValues: LabelFormValues = {
  invoice_no: '',
  order_no: '',
  article: '',
  color: '',
  finish: '',
  piece_no: '',
  qty_m: '',
  grade: '',
  label_date: dayjs(),
  inspector: '',
  qty_yd: '',
  print_copies: 10,
};

export function usePWJ30BPrint() {
  const [formValues, setFormValues] = useState<LabelFormValues>(defaultValues);
  const [previewResult, setPreviewResult] = useState<PrintResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewed, setPreviewed] = useState(false);
  const [printStatus, setPrintStatus] = useState<string>('');

  const buildRequest = useCallback((values: LabelFormValues): LabelRequest => ({
    invoice_no: values.invoice_no.trim(),
    order_no: values.order_no.trim(),
    article: values.article.trim(),
    color: values.color.trim(),
    finish: values.finish.trim(),
    piece_no: values.piece_no.trim(),
    qty_m: values.qty_m.trim(),
    grade: values.grade.trim(),
    label_date: values.label_date ? values.label_date.format('YYYY-MM-DD') : null,
    inspector: values.inspector.trim(),
    qty_yd: values.qty_yd.trim(),
    print_copies: values.print_copies || 1,
  }), []);

  const handleFieldChange = useCallback(<K extends keyof LabelFormValues>(
    field: K,
    value: LabelFormValues[K]
  ) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const handlePreview = useCallback(async () => {
    setLoading(true);
    setPreviewed(false);
    setPrintStatus('');
    try {
      const req = buildRequest(formValues);
      const result = await pwj30bApi.preview(req);
      setPreviewResult(result);
      setPreviewed(true);
      if (result.total_count === 0) {
        message.info('查無此資料');
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { details?: string[] } } };
      if (e.response?.status === 422) {
        const details = e.response.data?.details || [];
        message.error(details.join('\n') || '資料驗證失敗');
      } else {
        message.error('預覽標籤失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  }, [formValues, buildRequest]);

  const handlePrint = useCallback(async () => {
    setLoading(true);
    const startTime = new Date().toLocaleTimeString();
    setPrintStatus(`開始列印時間 :${startTime}`);
    try {
      const req = buildRequest(formValues);
      const result = await pwj30bApi.print(req);
      if (result.total_count === 0) {
        setPrintStatus(prev => `${prev}\n查無此資料!\n結束列印時間 :${new Date().toLocaleTimeString()}`);
        message.warning('查無此資料!');
      } else {
        setPreviewResult(result);
        setPreviewed(true);
        setPrintStatus(prev => `${prev}\n${new Date().toLocaleTimeString()} 列印結束!`);
        message.success(`列印完成，共 ${result.total_count} 份`);
        // Trigger browser print dialog
        setTimeout(() => window.print(), 200);
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { details?: string[] } } };
      if (e.response?.status === 422) {
        const details = e.response.data?.details || [];
        message.error(details.join('\n') || '資料驗證失敗');
      } else {
        message.error('列印失敗，請稍後再試');
      }
      setPrintStatus(prev => `${prev}\n列印失敗!`);
    } finally {
      setLoading(false);
    }
  }, [formValues, buildRequest]);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setPreviewResult(null);
    setPreviewed(false);
    setPrintStatus('');
  }, []);

  return {
    formValues,
    previewResult,
    loading,
    previewed,
    printStatus,
    handleFieldChange,
    handlePreview,
    handlePrint,
    handleReset,
  };
}
